import { useCallback, useEffect, useState } from 'react';
import { cookieDomains, trackingCookieNames } from 'utils/cookieConsent';

export type Consent = 'accepted' | 'rejected' | null;

// Broadcast on window so the banner, the trackers and the footer link agree.
const CHANGE_EVENT = 'cookie-consent-change';
export const OPEN_EVENT = 'cookie-consent-open';

export const openCookieSettings = () =>
  window.dispatchEvent(new Event(OPEN_EVENT));

// Google and Meta set their cookies on the site's domain or a parent of it.
const removeTrackingCookies = () => {
  const domains = cookieDomains(window.location.hostname);
  trackingCookieNames(document.cookie).forEach((name: string) =>
    domains.forEach((domain: string) => {
      document.cookie = `${name}=; Max-Age=0; path=/${
        domain ? `; domain=${domain}` : ''
      }`;
    }),
  );
};

const trackersRunning = () => {
  const w = window as unknown as { fbq?: unknown; gtag?: unknown };
  return typeof w.fbq === 'function' || typeof w.gtag === 'function';
};

const useCookieConsent = () => {
  const [consent, setConsent] = useState<Consent>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    fetch('/api/cookie')
      .then(response => response.json())
      .then(body => {
        if (!active) return;
        setConsent(body.consent ?? null);
        // A tracker may rewrite its cookie while the page unloads after a
        // reject, so clear leftovers on every load once rejected.
        if (body.consent === 'rejected') removeTrackingCookies();
      })
      .catch(() => undefined) // unknown counts as not consented
      .finally(() => {
        if (active) setLoaded(true);
      });
    const onChange = (event: Event) =>
      setConsent((event as CustomEvent<Consent>).detail);
    window.addEventListener(CHANGE_EVENT, onChange);
    return () => {
      active = false;
      window.removeEventListener(CHANGE_EVENT, onChange);
    };
  }, []);

  // Returns false when the choice could not be saved, so the banner stays
  // open and the visitor can try again.
  const choose = useCallback(
    async (choice: 'accepted' | 'rejected'): Promise<boolean> => {
      try {
        const response = await fetch('/api/cookie', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ consent: choice }),
        });
        if (!response.ok) return false;
      } catch (e) {
        return false;
      }
      if (choice === 'rejected') {
        removeTrackingCookies();
        // Trackers already running on this page (loaded here or before a
        // choice made in another tab) stop only on a fresh load.
        if (trackersRunning()) {
          window.location.reload();
          return true;
        }
      }
      window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: choice }));
      return true;
    },
    [],
  );

  return { consent, loaded, choose };
};

export default useCookieConsent;
